import { dwtUrlParamAliases, DWTUrlParamName, DWTUrlParams, dwtUrlParams } from './url-params';

export const searchParamDWTPrefix = 'caru-';

const deprecatedPrefixes = ['dwt-'];

const defaultSerializer = (v: unknown) => '' + v;

export class UrlService {
  public extractParams(currentSearch = window.location.search): Partial<DWTUrlParams> {
    const search = new URLSearchParams(currentSearch);
    const params: Partial<DWTUrlParams> = {};
    dwtUrlParams.forEach((param) => {
      const searchItem = this.getAliasedParamFromSearch(search, param.name, !param.dontTransformParamName);
      if (searchItem) {
        const paramValue = param.parser(searchItem);
        if (paramValue) {
          params[param.name] = paramValue as any;
        }
      }
    });
    return params;
  }

  public setParams(newParams: Partial<DWTUrlParams>) {
    const url = this.getNextUrl(window.location.href, newParams);
    if (url.href === window.location.href) {
      return;
    }
    window.history.pushState({}, document.title, url.href);
  }

  public getNextUrl(href: string, newParams: Partial<DWTUrlParams>, relativeHref?: boolean) {
    const url = relativeHref ? new URL(href, window.location.href) : new URL(href);
    const search = url.searchParams;
    Object.entries(newParams).forEach((entry) => {
      const [name, value] = entry;
      const param = dwtUrlParams.find((p) => p.name === name);
      this.deleteFromSearch(search, name);
      if (value) {
        const serializer = param?.serializer || defaultSerializer;
        const serializedValue = serializer(value);
        if (serializedValue) {
          const paramName = param?.dontTransformParamName ? name : this.getRawParamName(name);
          search.set(paramName, serializedValue);
        }
      }
    });
    return url;
  }

  public setParam(name: DWTUrlParamName, value: string | number | undefined) {
    this.setParams({ [name]: value });
  }

  public removeParam(name: DWTUrlParamName) {
    this.setParams({ [name]: undefined });
  }

  private deleteFromSearch(search: URLSearchParams, name: string) {
    const possiblePrefixes = ['', searchParamDWTPrefix, ...deprecatedPrefixes];
    const aliases = dwtUrlParamAliases[name as DWTUrlParamName] || [];
    const possibleNames = [name, ...aliases];

    possibleNames.forEach((possibleName) => possiblePrefixes.forEach((prefix) => search.delete(prefix + possibleName)));
  }

  private getAliasedParamFromSearch(search: URLSearchParams, name: DWTUrlParamName, usePrefix: boolean) {
    const param = this.getPrefixedParamFromSearch(search, name, usePrefix);
    if (!param) {
      const aliases = dwtUrlParamAliases[name];
      if (aliases) {
        return aliases.map((alias) => this.getPrefixedParamFromSearch(search, alias, usePrefix)).find((v) => !!v);
      }
    }
    return param;
  }

  private getPrefixedParamFromSearch(search: URLSearchParams, name: DWTUrlParamName | string, usePrefix: boolean) {
    if (!usePrefix) {
      return search.get(name);
    }

    return [searchParamDWTPrefix, ...deprecatedPrefixes]
      .map((prefix) => prefix + name)
      .map((param) => search.get(param))
      .find((value) => !!value);
  }

  private getRawParamName = (name: string) => searchParamDWTPrefix + name;
}

export const urlService = new UrlService();
